import { useCallback, useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useApi } from "../../hooks/useApi";
import { DocDefKeyIdSummary } from "../../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  sid: string;
  onAssigned: (keyId: string) => void;
};

export default function KeyIdSelectorModal({ isOpen, onClose, sid, onAssigned }: Props) {
  const { getDocDefKeyIds, putFileKeyId, putDocDefKeyId } = useApi();

  const [tabIndex, setTabIndex] = useState(0);
  const [keyIds, setKeyIds] = useState<DocDefKeyIdSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const [newFromKey, setNewFromKey] = useState("");
  const [newFamilyKey, setNewFamilyKey] = useState("");
  const [newTemplate, setNewTemplate] = useState("");
  const [newDocType, setNewDocType] = useState("PDF");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDocDefKeyIds();
      setKeyIds(data);
    } finally {
      setLoading(false);
    }
  }, [getDocDefKeyIds]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const assign = async (keyId: string) => {
    setSaving(true);
    try {
      await putFileKeyId(sid, keyId);
      onAssigned(keyId);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const createAndAssign = async () => {
    if (!newFromKey || !newFamilyKey || !newTemplate) return;
    setSaving(true);
    try {
      await putDocDefKeyId({
        doc_from_key: newFromKey,
        doc_family_key: newFamilyKey,
        doc_template: newTemplate,
        doc_type: newDocType,
      });
      const keyId = `${newFromKey}__${newFamilyKey}__${newTemplate}`;
      await putFileKeyId(sid, keyId);
      onAssigned(keyId);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const filtered = keyIds.filter((k) =>
    k.key_id.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change Key ID</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab>Select Existing</Tab>
              <Tab>Create New</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Input
                  placeholder="Filter key IDs..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  mb={3}
                  size="sm"
                />
                {loading ? (
                  <Spinner />
                ) : filtered.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">No key IDs found.</Text>
                ) : (
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Key ID</Th>
                        <Th>Type</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filtered.map((k) => (
                        <Tr key={k.key_id}>
                          <Td fontFamily="mono" fontSize="xs">{k.key_id}</Td>
                          <Td fontSize="xs">{k.doc_type}</Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="blue"
                              isLoading={saving}
                              onClick={() => assign(k.key_id)}
                            >
                              Select
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>
              <TabPanel px={0}>
                <VStack spacing={3} align="stretch">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Doc From Key</FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g. FRM"
                      value={newFromKey}
                      onChange={(e) => setNewFromKey(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Doc Family Key</FormLabel>
                    <Input
                      size="sm"
                      placeholder="e.g. FAM"
                      value={newFamilyKey}
                      onChange={(e) => setNewFamilyKey(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Doc Template</FormLabel>
                    <Input
                      size="sm"
                      placeholder="Template name"
                      value={newTemplate}
                      onChange={(e) => setNewTemplate(e.target.value.toUpperCase())}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Doc Type</FormLabel>
                    <Select
                      size="sm"
                      value={newDocType}
                      onChange={(e) => setNewDocType(e.target.value)}
                    >
                      <option value="PDF">PDF</option>
                      <option value="CSV">CSV</option>
                      <option value="XLSX">XLSX</option>
                      <option value="TIF">TIF</option>
                      <option value="OTHER">OTHER</option>
                    </Select>
                  </FormControl>
                  {newFromKey && newFamilyKey && newTemplate && (
                    <Text fontSize="xs" color="gray.500" fontFamily="mono">
                      Key ID: {newFromKey}__{newFamilyKey}__{newTemplate}
                    </Text>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            {tabIndex === 1 && (
              <Button
                colorScheme="blue"
                isLoading={saving}
                isDisabled={!newFromKey || !newFamilyKey || !newTemplate}
                onClick={createAndAssign}
              >
                Create &amp; Assign
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
